<script lang="ts">
  import { api } from '../lib/api'
  import { config } from '../lib/config'
  import { plural, referralUrl } from '../lib/format'
  import type { Account } from '../lib/types'
  import { ripple } from '../lib/ripple'
  import Notice from '../lib/components/Notice.svelte'
  import Loading from '../lib/components/Loading.svelte'

  let account = $state<Account | null>(null)
  let failed = $state(false)
  let copied = $state(false)

  const link = $derived(account ? referralUrl(config().marketing, account.referral.code) : '')
  const earned = $derived(account?.referral.monthsEarned ?? 0)
  const cap = $derived(account?.referral.monthsCap ?? 12)

  api
    .account()
    .then((result) => (account = result))
    .catch(() => (failed = true))

  async function copy() {
    try {
      await navigator.clipboard.writeText(link)
      copied = true
      setTimeout(() => (copied = false), 2500)
    } catch {
      // Clipboard access can be refused outright, and there is nothing to recover — the link is on
      // screen and selectable, which is what the Website offered and all this ever improves on.
      copied = false
    }
  }

  const shareText = 'Get started with Bird Journal now and get six months free of Bird Journal Premium:'
  const emailBody =
    "Bird Journal's a great way to keep your bird & wildlife observations. I'm really happy with it.\n\n" +
    'We each get six months free of Bird Journal Premium if you get started with this link: '
</script>

{#if failed}
  <Notice tone="error">We could not load your referral link just now. Please try again shortly.</Notice>
{:else if !account}
  <Loading rows={4} />
{:else}
  <p>
    Introduce a friend and you each get six months free of Bird Journal Premium, up to {cap} months.
  </p>
  <p>
    Bird Journal Premium provides a range of advanced analysis features including graphs, maps and
    reports. <a href="{config().marketing}/premium" data-external>Learn more about Bird Journal Premium</a>.
  </p>
  <p>
    If you already have Bird Journal Premium you can still earn free months. They are applied as credit if
    you are on an auto-renewing subscription.
  </p>

  <section class="earned">
    <h2>You've earned {earned} free {plural(earned, 'month')} of {cap}</h2>
    <div
      class="meter"
      role="progressbar"
      aria-valuenow={earned}
      aria-valuemin="0"
      aria-valuemax={cap}
      aria-label="Free months earned"
    >
      <div class="meter-fill" style:width="{(100 * earned) / cap}%"></div>
    </div>
  </section>

  <h2 class="ways">Ways to share</h2>

  <div class="field">
    <label class="field-label" for="link">Your personal link</label>
    <div class="sfield">
      <input id="link" type="text" value={link} readonly onfocus={(e) => e.currentTarget.select()} />
      <button class="btn btn-text btn-sm rip copy" use:ripple type="button" onclick={copy}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  </div>

  <div class="btn-row share">
    <a
      class="btn btn-outlined btn-sm rip"
      use:ripple
      href="https://x.com/intent/tweet?text={encodeURIComponent(shareText)}&url={encodeURIComponent(link)}"
      target="_blank"
      rel="noopener"
      data-external
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path
          d="M18.9 2.25h3.32l-7.25 8.29L23.75 21.75h-6.68l-5.23-6.84-5.99 6.84H2.53l7.76-8.87L2.06 2.25h6.85l4.73 6.25 5.46-6.25Zm-1.16 17.52h1.84L8.36 4.13H6.38l11.36 15.64Z"
        />
      </svg>
      Share on X
    </a>

    <a
      class="btn btn-outlined btn-sm rip"
      use:ripple
      href="https://www.facebook.com/sharer/sharer.php?u={encodeURIComponent(link)}"
      target="_blank"
      rel="noopener"
      data-external
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path
          d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.78-3.91 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.45 2.91h-2.33V22c4.78-.76 8.43-4.92 8.43-9.94Z"
        />
      </svg>
      Share on Facebook
    </a>

    <a
      class="btn btn-outlined btn-sm rip"
      use:ripple
      href="mailto:?subject={encodeURIComponent('Have a look at Bird Journal')}&body={encodeURIComponent(
        emailBody + link + '\n\n',
      )}"
      data-external
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5-8-5V6l8 5 8-5Z" />
      </svg>
      Share by email
    </a>
  </div>
{/if}

<style>
  .earned {
    margin: var(--space-8) 0;
  }
  .earned h2 {
    font-size: 1.05rem;
    margin-bottom: var(--space-3);
  }
  .ways {
    font-size: 1.05rem;
    margin-bottom: var(--space-4);
  }
  .copy {
    flex: none;
    margin-right: calc(var(--space-2) * -1);
  }
  .share {
    margin-top: var(--space-5);
  }
</style>
